import { AppError } from '@/shared/lib/appError'
import {
  type DeviceCapabilityReport,
  getModelRequirementsMB,
} from '@/shared/lib/deviceCapability'

export interface ModelInfo {
  /** Identificador exato exigido pelo `@mlc-ai/web-llm` (`prebuiltAppConfig`). */
  id: string
  /** Rótulo localizado em PT-BR para a UI. */
  label: string
  /** Tamanho aproximado dos pesos em MB (download). */
  sizeMB: number
  /** Indica se o modelo só roda com WebGPU; quando `false`, aceita fallback WASM. */
  requiresWebGPU: boolean
  /** Memória mínima estimada em MB para inferência sem OOM. */
  minMemoryMB: number
}

export const AVAILABLE_MODELS: ModelInfo[] = [
  {
    id: 'gemma-2-2b-it-q4f16_1-MLC',
    label: 'Gemma 2 2B — Recomendado',
    sizeMB: 1500,
    requiresWebGPU: true,
    minMemoryMB: 3000,
  },
  {
    id: 'Llama-3.2-1B-Instruct-q4f16_1-MLC',
    label: 'Llama 3.2 1B — Dispositivos com pouca RAM',
    sizeMB: 800,
    requiresWebGPU: false,
    minMemoryMB: 1500,
  },
]

export const findModel = (modelId: string): ModelInfo | undefined =>
  AVAILABLE_MODELS.find((model) => model.id === modelId)

export interface ModelCompatibilityResult {
  compatible: boolean
  reason?: string
}

export const isModelCompatible = (
  modelId: string,
  capability: DeviceCapabilityReport,
): ModelCompatibilityResult => {
  const model = findModel(modelId)
  if (!model) {
    return { compatible: false, reason: 'Modelo desconhecido.' }
  }

  if (model.requiresWebGPU && !capability.webgpu) {
    return { compatible: false, reason: 'Este modelo requer WebGPU neste dispositivo.' }
  }

  if (!model.requiresWebGPU && !capability.webgpu && !capability.wasmThreads) {
    return {
      compatible: false,
      reason: 'Sem WebGPU ou WASM Threads disponíveis para executar o modelo.',
    }
  }

  const requirementMB = getModelRequirementsMB(modelId)
  if (capability.estimatedMemoryMB < requirementMB) {
    return {
      compatible: false,
      reason: `Memória estimada (${capability.estimatedMemoryMB} MB) abaixo do mínimo (${requirementMB} MB).`,
    }
  }

  return { compatible: true }
}

/** Levanta `AppError('LOCAL_AI_UNSUPPORTED')` quando o modelo nao pode rodar no dispositivo. */
export const ensureModelCompatible = (
  modelId: string,
  capability: DeviceCapabilityReport,
): ModelInfo => {
  const result = isModelCompatible(modelId, capability)
  if (!result.compatible) {
    throw new AppError(result.reason ?? 'Modelo incompatível com o dispositivo.', {
      code: 'LOCAL_AI_UNSUPPORTED',
    })
  }
  return findModel(modelId) as ModelInfo
}

/** Quando ativo, a store usa um engine simulado (sem download de ~1.5GB). */
export const isLocalAIMockEnabled = (): boolean => {
  if (typeof import.meta === 'undefined') return false
  const env = (import.meta as { env?: Record<string, string | undefined> }).env
  return env?.VITE_SYNAP_MOCK_LOCAL_AI === 'true'
}

/** Capability sintética para dev/CI com `VITE_SYNAP_MOCK_LOCAL_AI=true`. */
export const buildMockLocalAICapability = (): DeviceCapabilityReport => ({
  webgpu: true,
  wasmThreads: true,
  estimatedMemoryMB: 8192,
  recommendedModel: 'gemma-2-2b-it-q4f16_1-MLC',
  canRunLocal: true,
})
